import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import {
  loadConsent,
  saveConsent,
  loadGoogleAnalytics,
  disableGoogleAnalytics,
  type CookieConsent,
} from '@/lib/analytics';
import { loadMetaPixel, disableMetaPixel } from '@/lib/metaPixel';

const NAVY = '#0E1A3C';
const NAVY_BORDER = 'rgba(255,255,255,0.10)';
const ROW_BG = 'rgba(255,255,255,0.04)';
const LIME = '#16A34A';
const TEXT = '#E6EAF2';
const TEXT_MUTED = '#A7B0C4';

type View = 'main' | 'settings';

const apply = (analytics: boolean, marketing: boolean) => {
  const consent: CookieConsent = {
    necessary: true,
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
  };
  saveConsent(consent);
  if (analytics) loadGoogleAnalytics();
  else disableGoogleAnalytics();
  if (marketing) loadMetaPixel();
  else disableMetaPixel();
  try {
    window.dispatchEvent(
      new CustomEvent('aimup-cookie-consent-changed', { detail: consent }),
    );
  } catch { /* ignore */ }
};

export const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<View>('main');
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);
  const navigate = useLocalizedNavigate();

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) setVisible(true);
    else {
      setAnalyticsOn(!!existing.analytics);
      setMarketingOn(!!existing.marketing);
    }

    const openHandler = () => {
      const c = loadConsent();
      setAnalyticsOn(!!c?.analytics);
      setMarketingOn(!!c?.marketing);
      setView('settings');
      setVisible(true);
    };
    window.addEventListener('aimup-open-cookie-settings', openHandler);
    return () => window.removeEventListener('aimup-open-cookie-settings', openHandler);
  }, []);

  const acceptAll = useCallback(() => { apply(true, true); setVisible(false); }, []);
  const rejectAll = useCallback(() => { apply(false, false); setVisible(false); }, []);
  const saveChoice = useCallback(() => { apply(analyticsOn, marketingOn); setVisible(false); }, [analyticsOn, marketingOn]);

  return (
    <AnimatePresence>
      {visible && (
        <>
          <style>{`
            .aimup-cc-link {
              color: ${LIME};
              font-weight: 600;
              text-decoration: none;
              border-bottom: 1px solid rgba(22,163,74,.45);
              background: none; padding: 0; cursor: pointer;
            }
            .aimup-cc-link:hover { border-bottom-color: ${LIME}; }
            .aimup-cc-btn { transition: filter .15s ease, transform .05s ease, background .15s ease; }
            .aimup-cc-btn:hover { filter: brightness(1.08); }
            .aimup-cc-btn:active { transform: translateY(1px); }
            .aimup-cc-btn:focus-visible, .aimup-cc-link:focus-visible, .aimup-cc-switch:focus-visible {
              outline: 2px solid ${LIME};
              outline-offset: 2px;
              border-radius: 8px;
            }
            @media (prefers-reduced-motion: reduce) {
              .aimup-cc-card { transition: none !important; animation: none !important; }
            }
          `}</style>
          <motion.div
            role="dialog"
            aria-label="Cookie-Einstellungen"
            aria-modal="false"
            className="aimup-cc-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              position: 'fixed', left: 16, bottom: 16, zIndex: 9999,
              width: 'min(460px, calc(100vw - 32px))',
              background: NAVY, color: TEXT,
              border: `1px solid ${NAVY_BORDER}`, borderRadius: 16, padding: 20,
              boxShadow: '0 18px 50px rgba(0,0,0,.45)',
              fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" fill="none" stroke={LIME} strokeWidth="1.6" />
                <circle cx="9" cy="9.5" r="1.1" fill={LIME} />
                <circle cx="14.5" cy="11" r="1.1" fill={LIME} />
                <circle cx="10.5" cy="14.5" r="1.1" fill={LIME} />
                <circle cx="15" cy="15" r="0.9" fill={LIME} />
              </svg>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#fff' }}>
                Cookie-Einstellungen
              </h2>
            </div>

            {view === 'main' ? (
              <>
                <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.55, color: TEXT_MUTED }}>
                  Wir verwenden notwendige Cookies, damit die Plattform funktioniert
                  (z.&nbsp;B. dein Login). Mit deiner Zustimmung nutzen wir zusätzlich
                  Google Analytics, um zu verstehen, wie AimUp genutzt wird, sowie den
                  Meta-Pixel (Facebook) zur Messung unserer Werbung. Mehr dazu in
                  unserer{' '}
                  <button
                    type="button"
                    className="aimup-cc-link"
                    onClick={() => { setVisible(false); navigate('/datenschutz'); }}
                  >
                    Datenschutzerklärung
                  </button>.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setView('settings')}
                    className="aimup-cc-btn"
                    style={{
                      background: 'transparent', color: TEXT_MUTED, border: 'none',
                      padding: '8px 6px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      textDecoration: 'underline', textUnderlineOffset: 3,
                    }}
                  >
                    Einstellungen
                  </button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={rejectAll}
                      className="aimup-cc-btn"
                      style={{
                        background: 'transparent', color: '#fff',
                        border: `1.5px solid ${NAVY_BORDER}`,
                        borderRadius: 10, padding: '10px 18px',
                        fontSize: 14, fontWeight: 700, cursor: 'pointer', minWidth: 120,
                      }}
                    >
                      Ablehnen
                    </button>
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="aimup-cc-btn"
                      style={{
                        background: LIME, color: '#fff', border: 'none',
                        borderRadius: 10, padding: '10px 18px',
                        fontSize: 14, fontWeight: 700, cursor: 'pointer', minWidth: 120,
                      }}
                    >
                      Alle akzeptieren
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.55, color: TEXT_MUTED }}>
                  Du entscheidest, welche Cookies du zulässt. Du kannst diese Auswahl
                  jederzeit über „Cookie-Einstellungen" im Footer ändern.
                </p>

                <ToggleRow
                  title="Notwendig"
                  desc="Erforderlich, damit die Plattform funktioniert (z. B. Login)."
                  checked
                  locked
                />
                <ToggleRow
                  title="Statistik · Google Analytics"
                  desc="Anonymisierte Reichweitenmessung, damit wir AimUp verbessern können."
                  checked={analyticsOn}
                  onChange={setAnalyticsOn}
                />
                <ToggleRow
                  title="Marketing · Meta-Pixel (Facebook)"
                  desc="Erlaubt Facebook/Instagram-Werbemessung über den Meta-Pixel, damit wir unsere Kampagnen auswerten können."
                  checked={marketingOn}
                  onChange={setMarketingOn}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="aimup-cc-btn"
                    style={{
                      background: 'transparent', color: '#fff',
                      border: `1.5px solid ${NAVY_BORDER}`,
                      borderRadius: 10, padding: '10px 18px',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Ablehnen
                  </button>
                  <button
                    type="button"
                    onClick={saveChoice}
                    className="aimup-cc-btn"
                    style={{
                      background: LIME, color: '#fff', border: 'none',
                      borderRadius: 10, padding: '10px 18px',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Auswahl speichern
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const ToggleRow: React.FC<{
  title: string;
  desc: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
}> = ({ title, desc, checked, locked, onChange }) => (
  <div
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      background: ROW_BG, border: `1px solid ${NAVY_BORDER}`,
      borderRadius: 12, padding: '12px 14px', marginBottom: 8,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{title}</div>
      <div style={{ color: TEXT_MUTED, fontSize: 12, marginTop: 2, lineHeight: 1.45 }}>{desc}</div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title}
      disabled={locked}
      onClick={() => !locked && onChange?.(!checked)}
      className="aimup-cc-switch"
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 44, height: 24, minHeight: 24, maxHeight: 24, borderRadius: 999,
        background: checked ? LIME : 'rgba(255,255,255,0.22)',
        border: 'none',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.6 : 1,
        transition: 'background .2s ease',
        padding: 0,
        boxSizing: 'border-box',
        lineHeight: 0,
        WebkitAppearance: 'none',
        appearance: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '50%',
          left: 3,
          width: 18, height: 18, borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.35)',
          transform: checked ? 'translate(20px, -50%)' : 'translate(0, -50%)',
          transition: 'transform .2s ease',
        }}
      />
    </button>
  </div>
);
